import { Injectable, inject, signal } from '@angular/core';
import { Company, CompanyService } from './company.service';
import { BehaviorSubject } from 'rxjs';

const STORAGE_KEY = 'active_company_id';

@Injectable({ providedIn: 'root' })
export class ActiveCompanyService {
  private companyService = inject(CompanyService);

  /** Active company id (client-side persisted). Backend remains authority on every request. */
  activeCompanyId = signal<string | null>(null);
  companies = signal<Company[]>([]);

  private companyChangedSubject = new BehaviorSubject<string | null>(null);
  /** Emits the next active company id whenever it changes. */
  companyChanged$ = this.companyChangedSubject.asObservable();

  loadFromStorage(): void {
    if (typeof window === 'undefined') return;
    const v = localStorage.getItem(STORAGE_KEY);
    this.activeCompanyId.set(v && v.trim() ? v.trim() : null);
  }

  persistActiveCompanyId(id: string | null): void {
    if (typeof window === 'undefined') return;
    if (!id) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(STORAGE_KEY, id);
  }

  setActiveCompanyId(id: string | null): void {
    const next = id && id.trim() ? id.trim() : null;
    const prev = this.activeCompanyId();
    this.activeCompanyId.set(next);
    this.persistActiveCompanyId(next);
    if (prev !== next) {
      this.companyChangedSubject.next(next);
    }
  }

  /**
   * Single source of truth for “which company should this page use”.
   * Returns null if not yet resolved (e.g. before companies load).
   */
  getActiveOrFallbackCompanyId(): string | null {
    const current = this.activeCompanyId();
    if (current) return current;
    const fallback = this.companies()[0]?.id ? String(this.companies()[0]!.id) : null;
    return fallback;
  }

  getActiveCompanyName(): string {
    const id = this.getActiveOrFallbackCompanyId();
    if (!id) return '';
    return this.companies().find((c) => c.id === id)?.name ?? '';
  }

  /**
   * Same as getActiveOrFallbackCompanyId(), but throws if none is available.
   * Use in pages where companyId is required to function.
   */
  getRequiredCompanyId(): string {
    const id = this.getActiveOrFallbackCompanyId();
    if (!id) {
      throw new Error('Company context missing');
    }
    return id;
  }

  /**
   * Fetch companies and ensure activeCompanyId is valid.
   * If missing/invalid, default to first company.
   */
  async refreshCompaniesAndEnsureActive(): Promise<void> {
    const list = await this.companyService.getAll();
    this.companies.set(list);

    const current = this.activeCompanyId();
    if (current && list.some(c => c.id === current)) {
      return;
    }
    const fallback = list[0]?.id ? String(list[0].id) : null;
    this.setActiveCompanyId(fallback);
  }

  /** Clear company context on logout — prevents prior user's company from leaking. */
  resetSessionState(): void {
    this.companies.set([]);
    this.activeCompanyId.set(null);
    this.persistActiveCompanyId(null);
    this.companyChangedSubject.next(null);
  }
}

